import { setUserSubscriptions } from "../../lib/api";
import { User, SubscriptionEnum, SubscriptionDisplayName } from "../../lib/types";
import { Checkbox } from "../ui/checkbox";
import { Dialog, DialogTrigger, DialogContent, DialogTitle, DialogDescription, DialogClose } from "../ui/dialog";
import { Label } from "../ui/label";
import { useState, useRef, useCallback } from "react";
import { Button } from "../ui/button";
import { DialogHeader, DialogFooter } from "../ui/dialog";
import { Input } from "../ui/input";
import { getSubscriptionDisplayName } from "../../lib/utils";

function UpdateSubscriptions({user, refreshRowData} : {user:User, refreshRowData: () => void}) {
    const [openUpdateSubscriptionsDialog, setOpenUpdateSubscriptionsDialog] = useState<boolean>(false);
    const [subscriptions, setSubscriptions] = useState<{subscription: SubscriptionEnum, checked: boolean}[]>(
      Object.values(SubscriptionEnum).map((subscription: SubscriptionEnum) => ({subscription: subscription, checked: user.subscriptions.includes(subscription)}))
    );
    const closeSubscriptionDialogButton = useRef<HTMLButtonElement>(null);
    const [userSubscriptionError, setUserSubscriptionError] = useState<string | null>(null);

    const handleOpenUpdateSubscriptionsDialog = useCallback((open: boolean) => {
      setSubscriptions(Object.values(SubscriptionEnum).map((subscription: SubscriptionEnum) => ({subscription: subscription, checked: user.subscriptions.includes(subscription)})));
      setUserSubscriptionError(null);
      setOpenUpdateSubscriptionsDialog(open);
    }, [user]);
  
    const handleSubscriptionChange = useCallback((subscription: SubscriptionEnum, checked: boolean) => {
      setSubscriptions(prev => {
        const prevSubscriptions = [...prev];
        const subscriptionIndex = prevSubscriptions.findIndex((row) => row.subscription === subscription);
        prevSubscriptions[subscriptionIndex] = {...prevSubscriptions[subscriptionIndex], checked: checked};
        return prevSubscriptions;
      });
    }, [subscriptions]);
  
    const handleSaveSubscriptions = useCallback(async () => {
        const subscriptionsString = subscriptions.filter(
        (subscription) => subscription.checked).map(
        (subscription) => subscription.subscription).join(',');
        try {
            await setUserSubscriptions(user.user_id, subscriptionsString);
            closeSubscriptionDialogButton.current?.click();
            refreshRowData();
        } catch (error) {
            console.error("Error setting subscriptions:", error);
            setUserSubscriptionError("Error setting subscriptions");
        } 
    }, [user, subscriptions, refreshRowData]);
  
    return (
      <Dialog open={openUpdateSubscriptionsDialog} onOpenChange={handleOpenUpdateSubscriptionsDialog}>
        <DialogTrigger asChild>
          <Button variant="outline" size="sm" title='Subscriptions'>{getSubscriptionDisplayName(user.subscriptions)}</Button>
        </DialogTrigger>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>Set subscriptions</DialogTitle>
            <DialogDescription className='hidden'>
              Set user subscriptions
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid grid-cols-4 items-center gap-4 row-span-2">
              <div className="col-span-1 row-span-2"> 
                <Label className="text-right">
                  Subscriptions
                </Label>
              </div>
              <div className="col-span-3 gap-1 row-span-2">
                {Object.values(SubscriptionEnum).map((subscription: SubscriptionEnum) => (
                  <div className="col-span-3 space-x-2 row-span-2">
                    <Checkbox id={subscription} checked={subscriptions.filter(
                      (s) => s.subscription === subscription)[0]?.checked || false} 
                    onCheckedChange={(checked: boolean) => handleSubscriptionChange(subscription, checked)}/>
                    <Label htmlFor={subscription}>{SubscriptionDisplayName[subscription]}</Label>
                  </div>
                ))}
              </div>
            </div>
            <div className="text-red-500" hidden={subscriptions.filter(
              (subscription) => subscription.checked).length > 0}>
              Atleast one subscription is required
            </div>
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="id" className="text-right text-gray-500">
                ID
              </Label>
              <Input id="id" value={user.user_id} className="col-span-3 text-gray-500" readOnly/>
            </div>
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="name" className="text-right text-gray-500">
                Name
              </Label>
              <Input id="name" value={user.user_name} className="col-span-3 text-gray-500" readOnly/>
            </div>
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="useremail" className="text-right text-gray-500">
                Email
              </Label>
              <Input id="useremail" value={user.user_email} className="col-span-3 text-gray-500" readOnly/>
            </div>
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="usertokens" className="text-right text-gray-500">
                Tokens
              </Label>
              <Input id="usertokens" type='number' value={user.user_tokens} className="col-span-3 text-gray-500" readOnly />
            </div>
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="subscriptions" className="text-right text-gray-500">
                Subscriptions
              </Label>
              <Input id="subscriptions" value={user.subscriptions} className="col-span-3 text-gray-500" readOnly />
            </div>
          </div>
          <DialogFooter>
            {userSubscriptionError && <p className="text-red-500">{userSubscriptionError}</p>}
            <DialogClose asChild>
              <Button type="button" variant="secondary" ref={closeSubscriptionDialogButton}>
                Close
              </Button>
            </DialogClose>
            <Button type="button" onClick={handleSaveSubscriptions} disabled={subscriptions.filter(
              (subscription) => subscription.checked).length === 0}>Save changes</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    )
  }

  export default UpdateSubscriptions;